import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/api/auth/authService';
import routes from '../utils/routes';

/**
 * Custom hook to handle authentication validation and token management
 * 
 * @returns {Object} Auth validation state and utility functions
 */
const useAuthValidation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isValidated, setIsValidated] = useState(false);
  
  // Ref to track if component is mounted
  const isMountedRef = useRef(true);
  
  // Validate token and handle authentication errors
  const validateToken = async () => {
    try {
      // First, check if we have a token at all to avoid unnecessary API calls
      const token = localStorage.getItem('token');
      if (!token) {
        //console.error('No authentication token found. Redirecting to login.');
        // Make sure all auth-related flags are cleared
        localStorage.removeItem('auth_redirect_triggered');
        localStorage.removeItem('ws_max_retries_reached');
        localStorage.removeItem('reconnect_attempts');
        navigate(routes.public.LOGIN);
        return false;
      }
      
      // Check for rate limiting flag with timestamp
      const rateLimitFlag = localStorage.getItem('auth_rate_limited');
      if (rateLimitFlag) {
        const rateLimitTime = parseInt(rateLimitFlag, 10);
        const currentTime = Date.now();
        
        // If we're still within the rate limit window (30 seconds)
        if (currentTime - rateLimitTime < 30000) {
          // console.log('Rate limited, waiting before retrying');
          setError('Too many requests. Please wait a moment before trying again.');
          setLoading(false);
          
          // Set a timer to clear the rate limit after the window expires
          if (isMountedRef.current) {
            const timeLeft = 30000 - (currentTime - rateLimitTime);
            // console.log(`Will retry auth in ${Math.ceil(timeLeft/1000)} seconds`);
            setTimeout(() => {
              if (isMountedRef.current) {
                // console.log('Rate limit window expired, clearing flag');
                localStorage.removeItem('auth_rate_limited');
                // Don't auto-retry to prevent potential loop
              }
            }, timeLeft);
          }
          return false;
        } else {
          // Rate limit window passed, clear the flag
          localStorage.removeItem('auth_rate_limited');
        }
      }
      
      // Add a flag to prevent multiple redirects
      if (localStorage.getItem('auth_redirecting')) {
        // console.log('Already redirecting, preventing loop');
        setLoading(false);
        return false;
      }
      
      // Validate JWT using /me endpoint
      // console.log('Validating JWT token with /me endpoint');
      await authService.getUserProfile();
      // console.log('JWT token validated successfully');
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsValidated(true);
        setLoading(false);
        setError(null);
      }
      return true;
    } catch (error) {
      // console.error('JWT validation failed:', error);
      
      // Only update state if component is still mounted
      if (!isMountedRef.current) return false;
      
      // Check if it's an API response error with status code
      if (error.response) {
        const status = error.response.status;
        
        switch (status) {
          case 401:
            // Unauthorized - invalid token
            // console.error('Authentication error: JWT token is invalid or expired');
            
            // Set a flag to prevent redirect loops
            localStorage.setItem('auth_redirecting', 'true');
            
            // Clear any existing token data since it's invalid
            localStorage.removeItem('token');
            localStorage.removeItem('refresh_token');
            
            // Show an error message briefly before redirecting
            setError('Your session has expired. Please log in again.');
            setLoading(false);
            
            // Give a short delay before redirecting so the user can see the message
            setTimeout(() => {
              localStorage.removeItem('auth_redirecting');
              navigate(routes.public.LOGIN, { 
                state: { 
                  errorType: 'session_expired',
                  errorMessage: 'Your session has expired. Please log in again.'
                } 
              });
            }, 1500);
            return false;
          
          case 403:
            // Forbidden - user doesn't have permission
            // console.error('Authorization error: Insufficient permissions');
            navigate(routes.public.LOGIN, { 
              state: { 
                errorType: 'permission_denied',
                errorMessage: 'You do not have permission to access this resource.'
              } 
            });
            return false;
            
          case 429:
            // Too many requests - rate limited
            // console.error('Rate limit error: Too many requests');
            
            // Store rate limit timestamp - use a longer timeout (60 seconds)
            localStorage.setItem('auth_rate_limited', Date.now().toString());
            
            // Display error instead of redirecting
            setError('Too many requests. Please wait a moment before trying again.');
            setLoading(false);
            
            // Only redirect if we need to completely reset the session
            if (error.response.data && error.response.data.resetSession) {
              navigate(routes.public.LOGIN, { 
                state: { 
                  errorType: 'rate_limit',
                  errorMessage: 'Too many requests. Please try again later.'
                } 
              });
            }
            return false;
            
          case 404:
            // Endpoint not found - This could mean the API structure has changed
            // console.error('API endpoint not found:', error.config.url);
            if (isMountedRef.current) {
              // If this is a refresh token endpoint, clear auth and redirect to login
              if (error.config.url.includes('refresh-token')) {
                localStorage.removeItem('token');
                localStorage.removeItem('refresh_token');
                setError('Authentication error. Please log in again.');
                setTimeout(() => {
                  navigate(routes.public.LOGIN);
                }, 1500);
                return false;
              }
              
              setError('API endpoint not found. Please contact support.');
              setLoading(false);
            }
            return false;
            
          case 500:
          case 502:
          case 503:
          case 504:
            // Server errors
            if (isMountedRef.current) {
              setError(`Server error (${status}). Please try again later.`);
              setLoading(false);
            }
            return false;
            
          default:
            // Other errors with status codes
            if (isMountedRef.current) {
              setError(`API error (${status}). Some features may not work properly.`);
              setLoading(false);
            }
            return false;
        }
      }
      
      // Network errors or other unknown errors
      if (isMountedRef.current) {
        // Check if it's a network error (no response)
        if (error.message && error.message.includes('Network Error')) {
          setError('Network error. Please check your internet connection.');
        } else {
          setError('Failed to validate authentication. Please check your connection.');
        }
        setLoading(false);
      }
      return false;
    }
  };
  
  // Run validation on component mount
  useEffect(() => {
    // Immediately validate token
    validateToken();
    
    // Cleanup function to set mounted flag to false
    return () => {
      isMountedRef.current = false;
    };
  }, [navigate]);
  
  const retry = () => {
    // Clear any rate limit flags
    localStorage.removeItem('auth_rate_limited');
    // Reset error state
    setError(null);
    // Start loading again
    setLoading(true);
    // Revalidate token
    setTimeout(() => {
      validateToken();
    }, 500);
  };
  
  return {
    loading,
    error,
    isValidated,
    setError,
    retry,
    validateToken
  };
};

export default useAuthValidation; 